import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ArrowLeft,
  Upload,
  FileImage,
  FileText,
  Scan,
  Check,
  X,
  Trash2,
  Plus,
  AlertCircle,
  Loader2,
  ShoppingCart,
  Package,
  History,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Files
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UNITS = [
  { value: 'kg', label: 'kg' },
  { value: 'g', label: 'g' },
  { value: 'l', label: 'L' },
  { value: 'ml', label: 'ml' },
  { value: 'units', label: 'units' },
  { value: 'pcs', label: 'pcs' },
  { value: 'bags', label: 'bags' },
  { value: 'drums', label: 'drums' },
  { value: 'boxes', label: 'boxes' },
];

export default function InvoiceUpload() {
  const { user, loading: authLoading } = useAuth();
  const { companyId, selectedCompany } = useCompany();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // State
  const [invoiceType, setInvoiceType] = useState('sales');
  const [files, setFiles] = useState([]); // Array of {id, file, preview, status, data, error}
  const [currentFileIndex, setCurrentFileIndex] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedFiles, setExpandedFiles] = useState(new Set());
  
  // Saved invoices history
  const [savedInvoices, setSavedInvoices] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Fetch saved invoices history
  const fetchInvoiceHistory = useCallback(async () => {
    if (!companyId) return;
    setLoadingHistory(true);
    
    try {
      const response = await fetch(`${API_URL}/api/invoices?company_id=${companyId}&limit=20`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setSavedInvoices(data.invoices || []);
      }
    } catch (error) {
      console.error('Failed to fetch invoice history:', error);
    } finally {
      setLoadingHistory(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId) {
      fetchInvoiceHistory();
    }
  }, [companyId, fetchInvoiceHistory]);

  const handleFileSelect = useCallback((e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;
    
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    const newFiles = [];
    
    for (const file of selectedFiles) {
      if (!validTypes.includes(file.type)) {
        toast.error(`${file.name}: Invalid file type`);
        continue;
      }
      
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: File too large (max 10MB)`);
        continue;
      }
      
      const fileObj = {
        id: `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file: file,
        preview: null,
        status: 'pending', // pending, extracting, extracted, saving, saved, error
        data: null,
        error: null
      };
      
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFiles(prev => prev.map(f => 
            f.id === fileObj.id ? { ...f, preview: e.target.result } : f
          ));
        };
        reader.readAsDataURL(file);
      }
      
      newFiles.push(fileObj);
    }
    
    if (newFiles.length > 0) {
      setFiles(prev => [...prev, ...newFiles]);
      toast.success(`Added ${newFiles.length} file(s)`);
      
      // Auto-expand first file if it's the only one
      if (files.length === 0 && newFiles.length === 1) {
        setExpandedFiles(new Set([newFiles[0].id]));
      }
    }
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [files.length]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      handleFileSelect({ target: { files: droppedFiles } });
    }
  }, [handleFileSelect]);

  const removeFile = (fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
    setExpandedFiles(prev => {
      const newSet = new Set(prev);
      newSet.delete(fileId);
      return newSet;
    });
  };

  const toggleFileExpanded = (fileId) => {
    setExpandedFiles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fileId)) {
        newSet.delete(fileId);
      } else {
        newSet.add(fileId);
      }
      return newSet;
    });
  };

  const extractSingleFile = async (fileObj) => {
    setFiles(prev => prev.map(f => 
      f.id === fileObj.id ? { ...f, status: 'extracting', error: null } : f
    ));
    
    try {
      const formData = new FormData();
      formData.append('file', fileObj.file);
      
      const response = await fetch(`${API_URL}/api/invoices/extract?invoice_type=${invoiceType}`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Extraction failed';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.detail || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      
      // Process extracted data
      const processedData = {
        companyName: data.company_name || '',
        supplierName: data.supplier_name || '',
        invoiceNumber: data.invoice_number || '',
        invoiceDate: data.invoice_date || new Date().toISOString().split('T')[0],
        receivedDate: new Date().toISOString().split('T')[0], // Default to today for received date
        items: (data.items || []).map((item, idx) => ({
          id: `item-${idx}`,
          item_name: item.item_name || '',
          quantity: item.quantity || 0,
          unit: item.unit || (invoiceType === 'sales' ? 'units' : 'kg')
        }))
      };
      
      setFiles(prev => prev.map(f => 
        f.id === fileObj.id ? { ...f, status: 'extracted', data: processedData } : f
      ));
      
      // Auto-expand
      setExpandedFiles(prev => new Set([...prev, fileObj.id]));
      
      return true;
    } catch (error) {
      setFiles(prev => prev.map(f => 
        f.id === fileObj.id ? { ...f, status: 'error', error: error.message } : f
      ));
      return false;
    }
  };

  const extractAllFiles = async () => {
    const pendingFiles = files.filter(f => f.status === 'pending');
    if (pendingFiles.length === 0) {
      toast.error('No files to extract');
      return;
    }
    
    setExtracting(true);
    let successCount = 0;
    
    for (const fileObj of pendingFiles) {
      const success = await extractSingleFile(fileObj);
      if (success) successCount++;
    }
    
    setExtracting(false);
    toast.success(`Extracted ${successCount}/${pendingFiles.length} files`);
  };

  const updateFileData = (fileId, field, value) => {
    setFiles(prev => prev.map(f => {
      if (f.id !== fileId) return f;
      return {
        ...f,
        data: { ...f.data, [field]: value }
      };
    }));
  };

  const updateFileItem = (fileId, itemId, field, value) => {
    setFiles(prev => prev.map(f => {
      if (f.id !== fileId) return f;
      return {
        ...f,
        data: {
          ...f.data,
          items: f.data.items.map(item => 
            item.id === itemId ? { ...item, [field]: value } : item
          )
        }
      };
    }));
  };

  const addItemToFile = (fileId) => {
    setFiles(prev => prev.map(f => {
      if (f.id !== fileId) return f;
      return {
        ...f,
        data: {
          ...f.data,
          items: [...f.data.items, {
            id: `item-${Date.now()}`,
            item_name: '',
            quantity: 0,
            unit: invoiceType === 'sales' ? 'units' : 'kg'
          }]
        }
      };
    }));
  };

  const removeItemFromFile = (fileId, itemId) => {
    setFiles(prev => prev.map(f => {
      if (f.id !== fileId) return f;
      return {
        ...f,
        data: {
          ...f.data,
          items: f.data.items.filter(item => item.id !== itemId)
        }
      };
    }));
  };

  const saveSingleFile = async (fileObj) => {
    if (!fileObj.data) return false;
    
    const { data } = fileObj;
    
    if (!data.invoiceNumber?.trim()) {
      toast.error(`${fileObj.file.name}: Invoice number required`);
      return false;
    }
    
    const validItems = data.items.filter(item => item.item_name.trim() && item.quantity > 0);
    if (validItems.length === 0) {
      toast.error(`${fileObj.file.name}: At least one valid item required`);
      return false;
    }
    
    setFiles(prev => prev.map(f => 
      f.id === fileObj.id ? { ...f, status: 'saving' } : f
    ));
    
    try {
      const payload = {
        invoice_type: invoiceType,
        company_id: companyId,
        company_name: invoiceType === 'sales' ? data.companyName : undefined,
        supplier_name: invoiceType === 'purchase' ? data.supplierName : undefined,
        invoice_number: data.invoiceNumber,
        invoice_date: data.invoiceDate,
        received_date: invoiceType === 'purchase' ? data.receivedDate : undefined,
        items: validItems.map(item => ({
          item_name: item.item_name,
          quantity: parseFloat(item.quantity),
          unit: item.unit
        }))
      };
      
      const response = await fetch(`${API_URL}/api/invoices/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Save failed');
      }
      
      setFiles(prev => prev.map(f => 
        f.id === fileObj.id ? { ...f, status: 'saved' } : f
      ));
      
      return true;
    } catch (error) {
      setFiles(prev => prev.map(f => 
        f.id === fileObj.id ? { ...f, status: 'error', error: error.message } : f
      ));
      return false;
    }
  };

  const saveAllFiles = async () => {
    const extractedFiles = files.filter(f => f.status === 'extracted');
    if (extractedFiles.length === 0) {
      toast.error('No extracted files to save');
      return;
    }
    
    setSaving(true);
    let successCount = 0;
    
    for (const fileObj of extractedFiles) {
      const success = await saveSingleFile(fileObj);
      if (success) successCount++;
    }
    
    setSaving(false);
    fetchInvoiceHistory();
    
    if (successCount > 0) {
      toast.success(`Saved ${successCount} invoice(s)`, {
        action: {
          label: invoiceType === 'sales' ? 'View Sales' : 'View Materials',
          onClick: () => navigate(invoiceType === 'sales' ? '/sales' : '/raw-materials')
        }
      });
    }
    
    // Remove saved files
    setTimeout(() => {
      setFiles(prev => prev.filter(f => f.status !== 'saved'));
    }, 1500);
  };

  const clearAll = () => {
    setFiles([]);
    setExpandedFiles(new Set());
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Pending' },
      extracting: { bg: 'bg-blue-100', text: 'text-blue-600', label: 'Extracting...' },
      extracted: { bg: 'bg-amber-100', text: 'text-amber-600', label: 'Review' },
      saving: { bg: 'bg-blue-100', text: 'text-blue-600', label: 'Saving...' },
      saved: { bg: 'bg-green-100', text: 'text-green-600', label: 'Saved' },
      error: { bg: 'bg-red-100', text: 'text-red-600', label: 'Error' }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`text-xs px-2 py-1 rounded ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const pendingCount = files.filter(f => f.status === 'pending').length;
  const extractedCount = files.filter(f => f.status === 'extracted').length;
  const savedCount = files.filter(f => f.status === 'saved').length;

  if (authLoading) {
    return (
      <Layout>
        <div className="loading-state">
          <div className="loading-spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-content" data-testid="invoice-upload-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="h-10 w-10 p-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Invoice Upload</h1>
              <p className="text-slate-500 mt-1">Batch upload invoices - {selectedCompany?.name}</p>
            </div>
          </div>
        </div>

        {/* Invoice Type Selector */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <button
            onClick={() => { setInvoiceType('sales'); clearAll(); }}
            className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
              invoiceType === 'sales' 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
          >
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              invoiceType === 'sales' ? 'bg-blue-100' : 'bg-slate-100'
            }`}>
              <ShoppingCart className={`w-5 h-5 ${invoiceType === 'sales' ? 'text-blue-600' : 'text-slate-500'}`} />
            </div>
            <div className="text-left">
              <p className={`font-semibold ${invoiceType === 'sales' ? 'text-blue-700' : 'text-slate-700'}`}>Sales Invoices</p>
              <p className="text-xs text-slate-500">Customer orders → Sales</p>
            </div>
          </button>
          <button
            onClick={() => { setInvoiceType('purchase'); clearAll(); }}
            className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
              invoiceType === 'purchase' 
                ? 'border-green-500 bg-green-50' 
                : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
          >
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              invoiceType === 'purchase' ? 'bg-green-100' : 'bg-slate-100'
            }`}>
              <Package className={`w-5 h-5 ${invoiceType === 'purchase' ? 'text-green-600' : 'text-slate-500'}`} />
            </div>
            <div className="text-left">
              <p className={`font-semibold ${invoiceType === 'purchase' ? 'text-green-700' : 'text-slate-700'}`}>Purchase Invoices</p>
              <p className="text-xs text-slate-500">Supplier bills → Raw Materials</p>
            </div>
          </button>
        </div>

        {/* Upload Area */}
        <div
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-slate-400 hover:bg-slate-50 transition-colors mb-6"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            onChange={handleFileSelect}
            className="hidden"
            multiple
          />
          <div className="flex flex-col items-center gap-3">
            <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center">
              <Files className="w-7 h-7 text-slate-400" />
            </div>
            <div>
              <p className="font-medium text-slate-700">Drop multiple invoices here</p>
              <p className="text-sm text-slate-500">or click to browse (JPEG, PNG, PDF)</p>
            </div>
          </div>
        </div>

        {/* Files Queue */}
        {files.length > 0 && (
          <div className="space-y-4">
            {/* Action Bar */}
            <div className="flex items-center justify-between bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-slate-700">
                  {files.length} file(s)
                </span>
                {pendingCount > 0 && (
                  <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                    {pendingCount} pending
                  </span>
                )}
                {extractedCount > 0 && (
                  <span className="text-xs bg-amber-100 text-amber-600 px-2 py-1 rounded">
                    {extractedCount} ready
                  </span>
                )}
                {savedCount > 0 && (
                  <span className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded">
                    {savedCount} saved
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearAll}
                >
                  Clear All
                </Button>
                {pendingCount > 0 && (
                  <Button
                    size="sm"
                    onClick={extractAllFiles}
                    disabled={extracting}
                    className="bg-slate-900 hover:bg-slate-800"
                  >
                    {extracting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Extracting...
                      </>
                    ) : (
                      <>
                        <Scan className="w-4 h-4 mr-2" />
                        Extract All ({pendingCount})
                      </>
                    )}
                  </Button>
                )}
                {extractedCount > 0 && (
                  <Button
                    size="sm"
                    onClick={saveAllFiles}
                    disabled={saving}
                    className={invoiceType === 'sales' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'}
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Save All ({extractedCount})
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>

            {/* File List */}
            <div className="space-y-3">
              {files.map((fileObj) => (
                <div 
                  key={fileObj.id} 
                  className={`bg-white border rounded-lg overflow-hidden ${
                    fileObj.status === 'saved' ? 'border-green-200 bg-green-50' :
                    fileObj.status === 'error' ? 'border-red-200' : 'border-slate-200'
                  }`}
                >
                  {/* File Header */}
                  <div 
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50"
                    onClick={() => toggleFileExpanded(fileObj.id)}
                  >
                    <div className="flex items-center gap-3">
                      {fileObj.preview ? (
                        <img src={fileObj.preview} alt="" className="w-10 h-10 rounded object-cover" />
                      ) : (
                        <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center">
                          <FileText className="w-5 h-5 text-slate-400" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{fileObj.file.name}</p>
                        <p className="text-xs text-slate-500">
                          {(fileObj.file.size / 1024).toFixed(0)} KB
                          {fileObj.data && ` • ${fileObj.data.items?.length || 0} items`}
                        </p>
                        {fileObj.status === 'error' && fileObj.error && (
                          <p 
                            className="text-xs text-red-600 mt-1 max-w-md truncate" 
                            title={fileObj.error}
                            data-testid={`file-error-message-${fileObj.id}`}
                          >
                            {fileObj.error}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(fileObj.status)}
                      {fileObj.status === 'pending' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); extractSingleFile(fileObj); }}
                          className="h-8 px-2"
                        >
                          <Scan className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); removeFile(fileObj.id); }}
                        className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                      {expandedFiles.has(fileObj.id) ? (
                        <ChevronUp className="w-4 h-4 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {expandedFiles.has(fileObj.id) && fileObj.data && (
                    <div className="border-t border-slate-200 p-4 bg-slate-50">
                      {/* Invoice Details */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                        {invoiceType === 'sales' ? (
                          <div>
                            <label className="text-xs text-slate-500">Customer</label>
                            <Input
                              value={fileObj.data.companyName}
                              onChange={(e) => updateFileData(fileObj.id, 'companyName', e.target.value)}
                              placeholder="Customer name"
                              className="h-9 text-sm"
                            />
                          </div>
                        ) : (
                          <div>
                            <label className="text-xs text-slate-500">Supplier</label>
                            <Input
                              value={fileObj.data.supplierName}
                              onChange={(e) => updateFileData(fileObj.id, 'supplierName', e.target.value)}
                              placeholder="Supplier name"
                              className="h-9 text-sm"
                            />
                          </div>
                        )}
                        <div>
                          <label className="text-xs text-slate-500">Invoice # *</label>
                          <Input
                            value={fileObj.data.invoiceNumber}
                            onChange={(e) => updateFileData(fileObj.id, 'invoiceNumber', e.target.value)}
                            placeholder="INV-001"
                            className="h-9 text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Invoice Date *</label>
                          <Input
                            type="date"
                            value={fileObj.data.invoiceDate}
                            onChange={(e) => updateFileData(fileObj.id, 'invoiceDate', e.target.value)}
                            className="h-9 text-sm"
                          />
                        </div>
                        {invoiceType === 'purchase' && (
                          <div>
                            <label className="text-xs text-slate-500">Received Date *</label>
                            <Input
                              type="date"
                              value={fileObj.data.receivedDate}
                              onChange={(e) => updateFileData(fileObj.id, 'receivedDate', e.target.value)}
                              className="h-9 text-sm"
                            />
                          </div>
                        )}
                        <div className="flex items-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => addItemToFile(fileObj.id)}
                            className="h-9 w-full"
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Add Item
                          </Button>
                        </div>
                      </div>

                      {/* Items */}
                      <div className="space-y-2">
                        {fileObj.data.items.map((item, idx) => (
                          <div key={item.id} className="flex items-center gap-2 bg-white p-2 rounded border border-slate-200">
                            <span className="w-6 text-center text-xs text-slate-400">{idx + 1}</span>
                            <Input
                              value={item.item_name}
                              onChange={(e) => updateFileItem(fileObj.id, item.id, 'item_name', e.target.value)}
                              placeholder="Item name"
                              className="flex-1 h-8 text-sm"
                            />
                            <Input
                              type="number"
                              step="0.01"
                              value={item.quantity}
                              onChange={(e) => updateFileItem(fileObj.id, item.id, 'quantity', parseFloat(e.target.value) || 0)}
                              placeholder="Qty"
                              className="w-20 h-8 text-sm"
                            />
                            <Select 
                              value={item.unit} 
                              onValueChange={(v) => updateFileItem(fileObj.id, item.id, 'unit', v)}
                            >
                              <SelectTrigger className="w-20 h-8 text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {UNITS.map(u => (
                                  <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeItemFromFile(fileObj.id, item.id)}
                              className="h-8 w-8 p-0 text-red-500"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>

                      {/* Single Save Button */}
                      {fileObj.status === 'extracted' && (
                        <div className="mt-4 flex justify-end">
                          <Button
                            size="sm"
                            onClick={() => saveSingleFile(fileObj)}
                            className={invoiceType === 'sales' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'}
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Save This Invoice
                          </Button>
                        </div>
                      )}

                      {/* Error Message */}
                      {fileObj.error && (
                        <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
                          {fileObj.error}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State / Instructions */}
        {files.length === 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800">How it works:</p>
                  <ol className="text-amber-700 mt-2 space-y-1 list-decimal list-inside">
                    <li>Select invoice type (Sales or Purchase)</li>
                    <li>Drop multiple invoice files at once</li>
                    <li>Click "Extract All" to process with AI</li>
                    <li>Review & edit extracted data</li>
                    <li>Click "Save All" to store in database</li>
                  </ol>
                </div>
              </div>
            </div>

            {/* Recent Uploads */}
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <History className="w-4 h-4" />
                Recent Uploads
              </h3>
              {loadingHistory ? (
                <div className="text-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                </div>
              ) : savedInvoices.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-4">No invoices uploaded yet</p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {savedInvoices.slice(0, 10).map((inv) => (
                    <div 
                      key={inv.invoice_id} 
                      className="flex items-center justify-between p-2 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer"
                      onClick={() => navigate(inv.invoice_type === 'sales' ? '/sales' : '/raw-materials')}
                    >
                      <div className="flex items-center gap-2">
                        {inv.invoice_type === 'sales' ? (
                          <ShoppingCart className="w-4 h-4 text-blue-500" />
                        ) : (
                          <Package className="w-4 h-4 text-green-500" />
                        )}
                        <div>
                          <p className="text-sm font-medium">{inv.invoice_number}</p>
                          <p className="text-xs text-slate-500">{inv.party_name} • {inv.items_count} items</p>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-300" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
